import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { Card, CardTitle } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import { DistributionBarGraph } from "../components/distribution-bar-graph";
import SankeyDiagram from "../components/sankey-diagram";
import DataTable from "../components/data-table";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import MobileDateRangeFields from "../components/mobile/mobile-date-range-fields";
import { DesktopOnly } from "../components/responsive";
import DateRangePicker from "../components/date-range-picker";
import SelectInput from "../components/select-input";
import SearchableSelectInput from "../components/searchable-select-input";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import {
  reportCurrencyTableColumns,
  reportCustomersTableColumns,
} from "../lib/table-columns";
import {
  getSalesReport,
  getInventoryReport,
  getMovementsReport,
  getFinancialReport,
  getCustomersReport,
  getCustomers,
  getSites,
  getWarehouses,
  SalesReport,
  InventoryReport,
  MovementsReport,
  FinancialReport,
  CustomersReport,
  ReportQueryDto,
  ReportPeriod,
} from "../lib/api";
import ReportingLoadingState from "../components/reporting-loading-state";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";
import { useIsMobile } from "../hooks/use-is-mobile";
import { MobileBarBreakdown, ReportSection, ReportStats } from "./reporting/report-blocks";

const PERIOD_OPTIONS = [
  { label: "Daily", value: ReportPeriod.DAILY },
  { label: "Weekly", value: ReportPeriod.WEEKLY },
  { label: "Monthly", value: ReportPeriod.MONTHLY },
  { label: "Yearly", value: ReportPeriod.YEARLY },
  { label: "Custom", value: ReportPeriod.CUSTOM },
];

const REPORT_TABS = ["Sales", "Inventory", "Movements", "Financial", "Customers"];

const calculateDateRangeForPeriod = (periodType: ReportPeriod): DateRange => {
  const today = new Date();
  const endDate = new Date(today);
  let startDate = new Date(today);

  switch (periodType) {
    case ReportPeriod.DAILY:
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case ReportPeriod.WEEKLY:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case ReportPeriod.MONTHLY:
      startDate = new Date(today);
      startDate.setMonth(today.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case ReportPeriod.YEARLY:
      startDate = new Date(today);
      startDate.setFullYear(today.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case ReportPeriod.CUSTOM:
      // For custom, return undefined to allow manual selection
      return { from: undefined, to: undefined };
    default:
      return { from: undefined, to: undefined };
  }

  return { from: startDate, to: endDate };
};

interface ReportFilters {
  period: ReportPeriod;
  dateRange: DateRange | undefined;
  customerId: string;
  siteId: string;
  warehouseId: string;
}

const buildQueryParams = ({
  period,
  dateRange,
  customerId,
  siteId,
  warehouseId,
}: ReportFilters): ReportQueryDto => {
  const query: ReportQueryDto = {};

  if (period) {
    query.period = period;
  }

  // Only include dates if period is CUSTOM or if we have a valid date range
  if (period === ReportPeriod.CUSTOM) {
    if (dateRange?.from && dateRange?.to) {
      query.startDate = format(dateRange.from, "yyyy-MM-dd");
      query.endDate = format(dateRange.to, "yyyy-MM-dd");
    }
  } else if (dateRange?.from && dateRange?.to) {
    // For non-custom periods, use the calculated date range
    query.startDate = format(dateRange.from, "yyyy-MM-dd");
    query.endDate = format(dateRange.to, "yyyy-MM-dd");
  }

  if (customerId) {
    query.customerId = customerId;
  }

  if (siteId) {
    query.siteId = siteId;
  }

  if (warehouseId) {
    query.warehouseId = warehouseId;
  }

  return query;
};

const formatTzs = (value: number) => `TZS ${value.toLocaleString()}`;

const Reporting: React.FC = () => {
  const isMobile = useIsMobile();
  const [currentTab, setCurrentTab] = useState("Sales");

  // Filter states
  const [period, setPeriod] = useState<ReportPeriod>(ReportPeriod.MONTHLY);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    calculateDateRangeForPeriod(ReportPeriod.MONTHLY),
  );
  const [customerId, setCustomerId] = useState<string>("");
  const [siteId, setSiteId] = useState<string>("");
  const [warehouseId, setWarehouseId] = useState<string>("");

  const customersQuery = useQuery({
    queryKey: ["customers", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getCustomers(1, 1000, ""),
  });

  const sitesQuery = useQuery({
    queryKey: ["sites", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getSites(1, 1000, ""),
  });

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getWarehouses(1, 1000, ""),
  });

  useEffect(() => {
    const e = customersQuery.error ?? sitesQuery.error ?? warehousesQuery.error;
    if (!e) return;
    toast.error(getBackendErrorMessage(e, "Error fetching filter options"));
  }, [customersQuery.error, sitesQuery.error, warehousesQuery.error]);

  const customers = customersQuery.data?.data ?? [];
  const sites = sitesQuery.data?.data ?? [];
  const warehouses = warehousesQuery.data?.data ?? [];

  // Update date range when period changes (except for CUSTOM)
  useEffect(() => {
    if (period !== ReportPeriod.CUSTOM) {
      setDateRange(calculateDateRangeForPeriod(period));
    } else {
      // Clear date range when switching to CUSTOM
      setDateRange(undefined);
    }
  }, [period]);

  // Desktop: reports only refresh when user clicks Apply/Refresh.
  const [appliedQuery, setAppliedQuery] = useState<ReportQueryDto>(() =>
    buildQueryParams({ period, dateRange, customerId, siteId, warehouseId }),
  );

  // Mobile filters live in a sheet without an Apply step, so changes apply immediately.
  useEffect(() => {
    if (!isMobile) return;
    setAppliedQuery(buildQueryParams({ period, dateRange, customerId, siteId, warehouseId }));
  }, [isMobile, period, dateRange, customerId, siteId, warehouseId]);

  const reportsQuery = useQuery({
    queryKey: ["reporting", appliedQuery],
    queryFn: async () => {
      const [sales, inventory, movements, financial, customers] = await Promise.all([
        getSalesReport(appliedQuery),
        getInventoryReport(appliedQuery),
        getMovementsReport(appliedQuery),
        getFinancialReport(appliedQuery),
        getCustomersReport(appliedQuery),
      ]);
      return { sales, inventory, movements, financial, customers };
    },
  });

  useEffect(() => {
    if (!reportsQuery.error) return;
    toast.error(getBackendErrorMessage(reportsQuery.error, "Failed to fetch reports"));
  }, [reportsQuery.error]);

  const salesReport: SalesReport | null = reportsQuery.data?.sales ?? null;
  const inventoryReport: InventoryReport | null = reportsQuery.data?.inventory ?? null;
  const movementsReport: MovementsReport | null = reportsQuery.data?.movements ?? null;
  const financialReport: FinancialReport | null = reportsQuery.data?.financial ?? null;
  const customersReport: CustomersReport | null = reportsQuery.data?.customers ?? null;

  const loading = reportsQuery.isLoading || customersQuery.isLoading || sitesQuery.isLoading || warehousesQuery.isLoading;

  const handleResetFilters = () => {
    setPeriod(ReportPeriod.MONTHLY);
    // Reset date range to initial calculated range for MONTHLY
    setDateRange(calculateDateRangeForPeriod(ReportPeriod.MONTHLY));
    setCustomerId("");
    setSiteId("");
    setWarehouseId("");
  };

  const applyFilters = () => {
    setAppliedQuery(buildQueryParams({ period, dateRange, customerId, siteId, warehouseId }));
  };

  if (loading && !isMobile) return <ReportingLoadingState />;

  const periodSelect = (
    <SelectInput
      value={period}
      options={PERIOD_OPTIONS}
      onChange={(value) => setPeriod(value as ReportPeriod)}
      placeholder="Select period"
      className="w-full"
    />
  );

  const dateRangeLabel =
    dateRange?.from && dateRange?.to
      ? `${format(dateRange.from, "MMM dd, yyyy")} - ${format(dateRange.to, "MMM dd, yyyy")}`
      : "No date range";

  const customerSelect = (
    <SearchableSelectInput
      value={customerId}
      options={customers.map((customer) => ({
        value: customer.id,
        label: customer.fullName,
      }))}
      onSelect={setCustomerId}
      defaultLabel="All Customers"
    />
  );

  const siteSelect = (
    <SearchableSelectInput
      value={siteId}
      options={sites.map((site) => ({
        value: site.id,
        label: site.name,
      }))}
      onSelect={setSiteId}
      defaultLabel="All Sites"
    />
  );

  const warehouseSelect = (
    <SearchableSelectInput
      value={warehouseId}
      options={warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }))}
      onSelect={setWarehouseId}
      defaultLabel="All Warehouses"
    />
  );

  const activeFilterCount = [
    period !== ReportPeriod.MONTHLY,
    !!customerId,
    !!siteId,
    !!warehouseId,
  ].filter(Boolean).length;

  const outflow = movementsReport?.byActivityType.find((a) => a.activityType === "OUTFLOW");
  const inflow = movementsReport?.byActivityType.find((a) => a.activityType === "INFLOW");

  return (
    <PageShell
      title="Reporting & Insights"
      description="Gain valuable insights into your equipment rentals, usage trends, and project performance."
      loading={loading}
      toolbar={
        <RefreshToolbarButton
          isRefreshing={reportsQuery.isFetching}
          onRefresh={() => reportsQuery.refetch()}
        />
      }
      mobile={{
        onRefresh: () => reportsQuery.refetch(),
        activeFilterCount,
        onResetFilters: handleResetFilters,
        filters: (
          <>
            <FilterField label="Period">{periodSelect}</FilterField>
            <FilterField label="Date range">
              {period === ReportPeriod.CUSTOM ? (
                <MobileDateRangeFields value={dateRange} onChange={setDateRange} />
              ) : (
                <div className="flex h-11 w-full items-center rounded-xl bg-gray-100 px-3.5 text-[15px] text-gray-600">
                  {dateRangeLabel}
                </div>
              )}
            </FilterField>
            <FilterField label="Customer">{customerSelect}</FilterField>
            <FilterField label="Site">{siteSelect}</FilterField>
            <FilterField label="Warehouse">{warehouseSelect}</FilterField>
          </>
        ),
      }}
    >
      <DesktopOnly>
        <Card>
          <Accordion type="single" collapsible defaultValue="" className="w-full">
            <AccordionItem value="filters" className="border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <CardTitle className="text-sm">Filters</CardTitle>
              </AccordionTrigger>
              <AccordionContent>
                <div className="px-6 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Period
                      </label>
                      {periodSelect}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Date Range
                      </label>
                      {period === ReportPeriod.CUSTOM ? (
                        <DateRangePicker
                          value={dateRange}
                          onChange={setDateRange}
                          className="w-full"
                        />
                      ) : (
                        <div className="w-full h-9 bg-gray-50 border border-gray-200 rounded-md px-3 flex items-center text-sm text-gray-600">
                          {dateRangeLabel}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Customer
                      </label>
                      {customerSelect}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Site
                      </label>
                      {siteSelect}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Warehouse
                      </label>
                      {warehouseSelect}
                    </div>

                    <div className="space-y-2 flex items-end gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 h-9"
                        onClick={handleResetFilters}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="default"
                        className="flex-1 h-9"
                        onClick={applyFilters}
                        disabled={reportsQuery.isFetching}
                      >
                        {reportsQuery.isFetching ? "Applying..." : "Apply Filters"}
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      </DesktopOnly>

      <TabBar>
        {REPORT_TABS.map((tab) => (
          <TabButton
            key={tab}
            label={tab}
            active={currentTab === tab}
            onClick={() => setCurrentTab(tab)}
          />
        ))}
      </TabBar>

      {/* Sales Report */}
      {currentTab === "Sales" && salesReport && (
        <div className="space-y-6">
          <ReportStats
            desktopClassName="grid grid-cols-1 md:grid-cols-2 gap-4"
            items={[
              {
                label: "Quotations",
                value: salesReport.quotations.total.toString(),
                description: `TZS ${salesReport?.quotations?.totals?.totalAmount?.toLocaleString()}`,
                icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Invoices",
                value: salesReport.invoices.total.toString(),
                description: `Paid: TZS ${salesReport?.invoices?.totals?.paid?.toLocaleString()}`,
                icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
              },
            ]}
          />

          {Object.keys(salesReport.byCurrency).length > 0 && (
            <ReportSection title="By Currency">
              <DataTable
                columns={reportCurrencyTableColumns}
                data={Object.entries(salesReport.byCurrency).map(
                  ([currency, data]) => ({
                    currency,
                    quotations: data.quotations.count.toString(),
                    invoices: data.invoices.count.toString(),
                    invoiceTotal: `${currency} ${data?.invoices?.totalAmount?.toLocaleString()}`,
                  }),
                )}
              />
            </ReportSection>
          )}
        </div>
      )}

      {/* Inventory Report */}
      {currentTab === "Inventory" && inventoryReport && (
        <div className="space-y-6">
          <ReportStats
            desktopClassName="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4"
            items={[
              {
                label: "Total Items",
                value: inventoryReport.totals.items.toString(),
                description: `Total qty: ${inventoryReport.totals.total} · Value: TZS ${(inventoryReport.totals.value || 0).toLocaleString()}`,
                icon: <Icons.Inventory className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Available",
                value: inventoryReport.totals.available.toString(),
                description: "Ready to rent",
                icon: <Icons.Tick className="w-5 h-5 text-green-600" />,
              },
              {
                label: "Rented",
                value: inventoryReport.totals.rented.toString(),
                description: "Currently out",
                icon: <Icons.Quotes className="w-5 h-5 text-blue-600" />,
              },
              {
                label: "Reserved",
                value: inventoryReport.totals.reserved.toString(),
                description: "Pending pickup",
                icon: <Icons.Clock className="w-5 h-5 text-orange-600" />,
              },
              {
                label: "Damaged",
                value: inventoryReport.totals.damaged.toString(),
                description: "Needs repair",
                icon: <Icons.Damaged className="w-5 h-5 text-red-600" />,
              },
              {
                label: "Lost",
                value: (inventoryReport.totals.lost || 0).toString(),
                description: "Written off",
                icon: <Icons.Lost className="w-5 h-5 text-gray-700" />,
              },
              {
                label: "Sold",
                value: (inventoryReport.totals.sold || 0).toString(),
                description: "Sold to customers",
                icon: <Icons.InventoryToSell className="w-5 h-5 text-emerald-600" />,
              },
            ]}
          />

          {inventoryReport.byWarehouse.length > 0 &&
            (isMobile ? (
              <MobileBarBreakdown
                title="Inventory by Warehouse"
                stacked
                rows={inventoryReport.byWarehouse.map((warehouse) => ({
                  label: warehouse.warehouseName,
                  values: [
                    { label: "Available", value: warehouse.available, color: "#86efac" },
                    { label: "Rented", value: warehouse.rented, color: "#60a5fa" },
                    { label: "Reserved", value: warehouse.reserved, color: "#fbbf24" },
                    { label: "Damaged", value: warehouse.damaged, color: "#f87171" },
                    { label: "Lost", value: warehouse.lost || 0, color: "#a1a1aa" },
                    { label: "Sold", value: warehouse.sold || 0, color: "#34d399" },
                  ],
                }))}
              />
            ) : (
              <HorizontalBarGraph
                graphTitle="Inventory by Warehouse"
                data={inventoryReport.byWarehouse.map((warehouse) => ({
                  category: warehouse.warehouseName,
                  gap: 0.2,
                  values: [
                    {
                      label: "Available",
                      value: warehouse.available,
                      fillColor: "#86efac",
                    },
                    {
                      label: "Rented",
                      value: warehouse.rented,
                      fillColor: "#60a5fa",
                    },
                    {
                      label: "Reserved",
                      value: warehouse.reserved,
                      fillColor: "#fbbf24",
                    },
                    {
                      label: "Damaged",
                      value: warehouse.damaged,
                      fillColor: "#f87171",
                    },
                    {
                      label: "Lost",
                      value: warehouse.lost || 0,
                      fillColor: "#a1a1aa",
                    },
                    {
                      label: "Sold",
                      value: warehouse.sold || 0,
                      fillColor: "#34d399",
                    },
                  ],
                }))}
                selectedOption="Overview"
                placeholder="Warehouses"
                onOptionChange={() => { }}
                selectOptions={[]}
              />
            ))}
        </div>
      )}

      {/* Movements Report */}
      {currentTab === "Movements" && movementsReport && (
        <div className="space-y-6">
          <ReportStats
            desktopClassName="grid grid-cols-1 md:grid-cols-4 gap-4"
            items={[
              {
                label: "Total Movements",
                value: movementsReport.totals.movements.toString(),
                description: `Closed: ${movementsReport.totals.closedMovements}`,
                icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Items",
                value: movementsReport.totals.items.toString(),
                description: `Quantity: ${movementsReport.totals.quantity}`,
                icon: <Icons.Inventory className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Outflow",
                value: (outflow?.count || 0).toString(),
                description: `Items: ${outflow?.items || 0}`,
                icon: <Icons.Billing className="w-5 h-5 text-blue-600" />,
              },
              {
                label: "Inflow",
                value: (inflow?.count || 0).toString(),
                description: `Items: ${inflow?.items || 0}`,
                icon: <Icons.Download className="w-5 h-5 text-green-600" />,
              },
            ]}
          />

          {isMobile ? (
            <>
              {movementsReport.byActivitySubType.length > 0 && (
                <MobileBarBreakdown
                  title="Movements by Sub Type"
                  rows={movementsReport.byActivitySubType.map((activity) => ({
                    label: activity.activitySubType,
                    values: [{ label: "Movements", value: activity.count, color: "#60a5fa" }],
                  }))}
                />
              )}
              {movementsReport.byStatus.length > 0 && (
                <MobileBarBreakdown
                  title="Movements by Status"
                  rows={movementsReport.byStatus.map((status) => ({
                    label: status.status,
                    values: [{ label: "Movements", value: status.count, color: "#86efac" }],
                  }))}
                />
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {movementsReport.byActivitySubType.length > 0 && (
                <DistributionBarGraph
                  title="Movements by Sub Type"
                  data={movementsReport.byActivitySubType.map((activity) => ({
                    label: activity.activitySubType,
                    value: activity.count,
                  }))}
                />
              )}

              {movementsReport.byStatus.length > 0 && (
                <SankeyDiagram
                  title="Movements by Status"
                  data={{
                    nodes: [
                      {
                        name: "All Movements",
                        percent: 100,
                        absoluteValue: movementsReport.totals.movements,
                      },
                      ...movementsReport.byStatus.map((status) => ({
                        name: status.status,
                        percent:
                          (status.count /
                            movementsReport.totals.movements) *
                          100,
                        absoluteValue: status.count,
                      })),
                    ],
                    links: movementsReport.byStatus.map(
                      (status, index) => ({
                        source: 0,
                        target: index + 1,
                        value: status.count,
                        sourceName: "All Movements",
                      }),
                    ),
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Financial Report */}
      {currentTab === "Financial" && financialReport && (
        <div className="space-y-6">
          <ReportStats
            desktopClassName="grid grid-cols-1 md:grid-cols-3 gap-4"
            items={[
              {
                label: "Quotations",
                value: financialReport.totals.quotations.count.toString(),
                description: `TZS ${financialReport?.totals?.quotations?.totalAmount?.toLocaleString()}`,
                icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Invoices",
                value: financialReport.totals.invoices.count.toString(),
                description: `Total: TZS ${financialReport?.totals?.invoices?.totalAmount?.toLocaleString()}`,
                icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Net Revenue",
                value: `TZS ${financialReport?.totals?.netRevenue?.toLocaleString()}`,
                description: `Paid: TZS ${financialReport?.totals?.invoices?.paid?.toLocaleString()}`,
                icon: (
                  <Icons.RentedOut
                    className={`w-5 h-5 ${financialReport.totals.netRevenue >= 0
                      ? "text-green-600"
                      : "text-red-600"
                      }`}
                  />
                ),
              },
            ]}
          />

          {financialReport.byCustomer.length > 0 &&
            (isMobile ? (
              <MobileBarBreakdown
                title="Revenue by Customer"
                formatValue={formatTzs}
                rows={financialReport.byCustomer.map((customer) => ({
                  label: customer.customerName,
                  values: [
                    { label: "Quotation", value: customer.quotationAmount, color: "#93c5fd" },
                    { label: "Invoice", value: customer.invoiceAmount, color: "#bfdbfe" },
                    { label: "Paid", value: customer.paidAmount, color: "#86efac" },
                  ],
                }))}
              />
            ) : (
              <HorizontalBarGraph
                graphTitle="Revenue by Customer"
                data={financialReport.byCustomer.map((customer) => ({
                  category: customer.customerName,
                  gap: 0.2,
                  values: [
                    {
                      label: "Quotation",
                      value: customer.quotationAmount,
                      fillColor: "#93c5fd",
                    },
                    {
                      label: "Invoice",
                      value: customer.invoiceAmount,
                      fillColor: "#bfdbfe",
                    },
                    {
                      label: "Paid",
                      value: customer.paidAmount,
                      fillColor: "#86efac",
                    },
                  ],
                }))}
                selectedOption="Overview"
                placeholder="Customers"
                onOptionChange={() => { }}
                selectOptions={[]}
              />
            ))}
        </div>
      )}

      {/* Customers Report */}
      {currentTab === "Customers" && customersReport && (
        <div className="space-y-6">
          <ReportStats
            desktopClassName="grid grid-cols-2 md:grid-cols-4 gap-4"
            items={[
              {
                label: "Total Customers",
                value: customersReport.totals.customers.toString(),
                description: "Active customers",
                icon: <Icons.Clients className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Sites",
                value: customersReport.totals.sites.toString(),
                description: "Locations",
                icon: <Icons.Location className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Quotations",
                value: customersReport.totals.quotations.toString(),
                description: "Created",
                icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
              },
              {
                label: "Invoices",
                value: customersReport.totals.invoices.toString(),
                description: "Generated",
                icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
              },
            ]}
          />

          {customersReport.customers.length > 0 && (
            <ReportSection title="Customer Details">
              <DataTable
                columns={reportCustomersTableColumns}
                data={customersReport.customers.map((customer) => ({
                  customerName: customer.customerName,
                  type: customer.type,
                  sites: customer.sites.toString(),
                  quotations: customer.quotations.count.toString(),
                  invoices: customer.invoices.count.toString(),
                  invoiceTotal: `TZS ${customer?.invoices?.totalAmount?.toLocaleString()}`,
                  paid: `TZS ${customer?.invoices?.paid?.toLocaleString()}`,
                  unpaid: `TZS ${customer?.invoices?.unpaid?.toLocaleString()}`,
                }))}
              />
            </ReportSection>
          )}
        </div>
      )}
    </PageShell>
  );
};

export default Reporting;
